import { useCallback } from "react";
import { MenuItem } from "@progress/kendo-react-layout";
import { GridCellProps } from "@progress/kendo-react-grid";
import { routes } from "../../app/routes";
import { useSelector } from "react-redux";
import { getUserInfo, isSysAdmin, isTenantAdmin, RootState } from "../../app";
import { OrganizationModel } from "../../data/entities";
import { usePageActions } from "../";
import { PermissionDefinitions } from "../../security";
import { OrganizationService } from "../../services";
import { useCRUDHandlers,
  useGridActions,
  usePermission,
  useSetPageTitle,
  useSharedState } from "../../hooks";
import { AddOrganizationModal } from "./components";
import { Button,
  IconAndText,
  IconDefinitions,
  TitlelessCard,
  ODataGrid,
  ResponsiveGridColumn,
  Breakpoints,
  ConfirmationModal,
  Icon } from "../../components";
import { createODataQueryFilter } from "../../utils";

export function OrganizationsIndexPage() {

  const gridData = useSharedState<Array<OrganizationModel>>([]);
  const organizationService = new OrganizationService();
  const { onClick, onSelect, showDelete, deleteItemState, showAddState, showDeleteState } = useGridActions<OrganizationModel>(routes.organization.details);
  const { gridRef, getItems, addItem, deleteItem } = useCRUDHandlers<OrganizationModel>(organizationService, "Organization", showAddState);
  const tenantId = useSelector((state: RootState) => state.authentication.tenantId);
  const canAddOrganization = usePermission(PermissionDefinitions.organization.create(tenantId!));
  const canDeleteOrganization = usePermission(PermissionDefinitions.organization.delete(tenantId!));
  
  const tenantAdmin = useSelector(isTenantAdmin());
  const systemAdmin = useSelector(isSysAdmin());
  const userInfo = useSelector(getUserInfo());  
  const userOrgs = userInfo?.organizationIds;

  useSetPageTitle("Organizations", []);
  usePageActions(
    canAddOrganization && (
      <Button
        onClick={() => {
          showAddState.setter(true);
        }}
      >
        <IconAndText iconName={IconDefinitions.add} text="Add" />
      </Button>
    )
  , [canAddOrganization]);

  const commandsCell = useCallback(
    (props: GridCellProps) => (
      <>
      {canDeleteOrganization && (
        <td className="text-center">
          <Icon
            iconName={IconDefinitions.delete}
            className="text-danger"
            onClick={() => showDelete(props.dataItem)}
          />
        </td>
        )}
      </>
    ),
    [showDelete, canDeleteOrganization]
  );

  return (
    <TitlelessCard bodyClassName="p-0">
      <ODataGrid
        ref={gridRef}
        getData={(oDataQuery) => {
          if(tenantAdmin || systemAdmin){
            return getItems(`${oDataQuery}`)
          }else{
             const orgFilter = [`(${userOrgs?.map(id => `id eq ${id}`).join(' or ')})`]
            const filterQuery = createODataQueryFilter(oDataQuery, orgFilter);
            return getItems(filterQuery)
          }
         
        }}
        dataState={gridData}
        onRowClick={onClick}
        onRowMenuSelect={onSelect}
        sort={[{ field: "name", dir: "asc"}]}
        scrollable="scrollable"
        pageSize={10}
      >
        <ResponsiveGridColumn
          title="Name"
          field="name"
          sortable={true}
        />
        {canDeleteOrganization && (
          <ResponsiveGridColumn
            className="column-url"
            field="url"
            title=" "
            sortable={false}
            filterable={false}
            headerCell={() => null}
            width={50}
            breakpoint={Breakpoints.sm}
            cell={canDeleteOrganization === true ? commandsCell : undefined}
          />
       )}
        <MenuItem
          text="Open"
          data={{ action: "open" }}
          icon="hyperlink-open"
        />
        <MenuItem
          text="Open Tab"
          data={{ action: "opentab" }}
          icon="windows"
        />
        {
          canDeleteOrganization && (
            <MenuItem
              text="Delete"
              data={{ action: "delete" }}
              icon={IconDefinitions.delete}
            />
          )
        }
      </ODataGrid>
      <AddOrganizationModal
        showAddState={showAddState}
        showState={showAddState}
        onSubmit={addItem}
      />
      <ConfirmationModal
        showState={showDeleteState}
        title={`Delete ${deleteItemState.value?.name}`}
        message={`You are about to delete this organization, it cannot be undone. Are you sure you want to delete ${deleteItemState.value.name}?`}
        confirmText="Delete"
        onConfirm={() => deleteItem(deleteItemState.value)}
      />
    </TitlelessCard>
  )
}

export default OrganizationsIndexPage;
